import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { TiltCard, scrollAnimationVariants } from './InteractiveEffects';

const Portfolio = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Real project images - Add more images here as needed
  const portfolioImages = [
    {
      id: 1,
      src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/9ww2abqi_WhatsApp%20Image%202026-03-27%20at%201.37.11%20PM%20%281%29.jpeg',
      title: 'Custom Aluminium Frame',
      category: 'Aluminium Works',
      description: 'Bespoke curved aluminium frame structure for luxury villa entrance.',
    },
    {
      id: 2,
      src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/b4ekb6zz_WhatsApp%20Image%202026-03-27%20at%201.37.11%20PM.jpeg',
      title: 'Luxury Bathroom Mirror',
      category: 'Glass Works',
      description: 'Full-wall mirror installation with floating vanity in premium residence.',
    },
    {
      id: 3,
      src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/8vxqmnt6_WhatsApp%20Image%202026-03-27%20at%201.37.12%20PM%20%283%29.jpeg',
      title: 'Glass Shower Enclosure',
      category: 'Glass Works',
      description: 'Frameless glass shower partition with premium marble finishes.',
    },
    {
      id: 4,
      src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/9mfegti7_WhatsApp%20Image%202026-03-27%20at%201.37.12%20PM%20%282%29.jpeg',
      title: 'Mirror Installation',
      category: 'Glass Works',
      description: 'Large format mirror installation for master bathroom.',
    },
    {
      id: 5,
      src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/wbu7z5kg_WhatsApp%20Image%202026-03-27%20at%201.37.12%20PM%20%281%29.jpeg',
      title: 'Aluminium Door Installation',
      category: 'Aluminium Works',
      description: 'Classic aluminium French doors with glass panels for villa entrance.',
    },
  ];

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(portfolioImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % portfolioImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(portfolioImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + portfolioImages.length) % portfolioImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(portfolioImages[newIndex]);
  };

  return (
    <section 
      id="portfolio" 
      className="py-24 md:py-32 px-6 relative"
      data-testid="portfolio-section"
      ref={ref}
    >
      {/* Glassy background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-blue-50/50 to-white/30"></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div 
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="text-xs tracking-[0.2em] uppercase font-semibold text-[#25D366] block mb-4"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Our Work
          </motion.span>
          <motion.h2 
            className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-slate-800 mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Projects Portfolio
          </motion.h2>
          <motion.p 
            className="text-slate-600 max-w-2xl mx-auto text-lg"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Browse through our completed projects showcasing quality craftsmanship in aluminium, glass, and maintenance works across the UAE.
          </motion.p>
        </motion.div>

        {/* Portfolio Grid with 3D Tilt Effect */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          {portfolioImages.map((image, index) => (
            <TiltCard
              key={image.id}
              className="aspect-square"
              intensity={10}
            >
              <motion.div
                className="group relative w-full h-full overflow-hidden rounded-2xl cursor-pointer bg-white/50 backdrop-blur-sm shadow-lg border border-white/50"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                onClick={() => openLightbox(index)}
                data-testid={`portfolio-item-${image.id}`}
              >
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-sky-900 via-sky-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <span className="text-xs tracking-[0.15em] uppercase text-[#25D366] mb-2 font-medium">
                      {image.category}
                    </span>
                    <h3 
                      className="text-lg font-medium text-white mb-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {image.title}
                    </h3>
                    <p className="text-sky-200 text-sm line-clamp-2">
                      {image.description}
                    </p>
                  </div>
                  
                  {/* Zoom Icon */}
                  <motion.div 
                    className="absolute top-4 right-4"
                    initial={{ scale: 0 }}
                    whileHover={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <ZoomIn size={20} className="text-white" />
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </TiltCard>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <a
            href="https://wa.me/971566832840?text=Hello%2C%20I%27d%20like%20to%20discuss%20a%20project%20similar%20to%20your%20portfolio"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp inline-flex"
            data-testid="portfolio-cta"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Discuss Your Project
          </a>
        </motion.div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            data-testid="portfolio-lightbox"
          >
            {/* Close button */}
            <button
              className="absolute top-6 right-6 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              onClick={closeLightbox}
              data-testid="lightbox-close"
            >
              <X size={24} />
            </button>

            {/* Navigation arrows */}
            <button
              className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              data-testid="lightbox-next"
            >
              <ChevronRight size={24} />
            </button>

            {/* Image container */}
            <motion.div
              className="relative max-w-5xl max-h-[85vh] w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.title}
                className="w-full h-full object-contain rounded-sm"
              />
              
              {/* Image info */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <span className="text-xs tracking-[0.15em] uppercase text-[#25D366] mb-2 block font-medium">
                  {selectedImage.category}
                </span>
                <h3 
                  className="text-xl font-medium text-white mb-2"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {selectedImage.title}
                </h3>
                <p className="text-slate-300 text-sm">
                  {selectedImage.description}
                </p>
              </div>

              {/* Image counter */}
              <div className="absolute top-4 left-4 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-sm">
                {currentIndex + 1} / {portfolioImages.length}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
